import {
  FeedbackResponseDTO,
  LiveClassFeedbackService,
} from '@openeducation/oe-it-services';
import type { LiveClassPendingFeedback } from '@openeducation/oe-it-shared-types';
import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { useEnvironmentContext } from '../environment/environment.provider';

export interface UseLiveClassActiveFeedbackProps {
  classroomId: number | undefined;
  teacherFacultyId: string | undefined;
}

export const useLiveClassActiveFeedback = ({
  teacherFacultyId,
  classroomId,
}: UseLiveClassActiveFeedbackProps) => {
  const { environment } = useEnvironmentContext();
  const {
    data: teacherActiveLiveClassFeedback,
    isLoading: isLoadingTeacherActiveLiveClassFeedback,
  } = useQuery<LiveClassPendingFeedback[]>(
    [`teacher-active-live-class-feedback`, teacherFacultyId],
    () =>
      LiveClassFeedbackService.getLiveClassPendingFeedbackByFacultyId(
        environment,
        teacherFacultyId
      ),
    {
      enabled: !!teacherFacultyId,
    }
  );

  const studentFeedback = useMemo(
    () =>
      teacherActiveLiveClassFeedback?.find(
        (lcFeedback) => lcFeedback.classroomId === classroomId
      ),
    [classroomId, teacherActiveLiveClassFeedback]
  );

  const {
    data: liveClassFeedback,
    isLoading: isLoadingLiveClassFeedback,
    isError: isErrorLiveClassFeedback,
  } = useQuery<FeedbackResponseDTO>(
    ['live-class-active-feedback', studentFeedback?.uuid],
    () => {
      return LiveClassFeedbackService.getLiveClassFeedbackByUUID(
        environment,
        studentFeedback?.uuid
      );
    },
    {
      enabled: !!studentFeedback?.uuid,
    }
  );

  return {
    liveClassFeedback,
    isLoadingLiveClassFeedback:
      isLoadingLiveClassFeedback || isLoadingTeacherActiveLiveClassFeedback,
    isErrorLiveClassFeedback,
  };
};
